// RegenHistoryEntry — Sprint 19BH Day 1 Step 1.4 · 2-phase regen audit trail.
//
// Why this exists (v4-rewrite.md §10.3):
//   v4 promotes regen to a product-core surface. Every regen is a 2-phase
//   transaction:
//
//     Phase 1 (POST /ingest/regenerate):
//       acquire lock → gather context per manifest.regenContext → LLM call
//       → return { regenId, proposedItems, beforeSnapshot, diffSummary }
//       → INSERT regen_history row, status='pending'
//
//     Phase 2 (POST /ingest/regenerate-confirm):
//       payload { regenId, decision: 'apply'|'reject', editedItems? }
//       → apply: mutate results, status='applied'
//       → reject: status='rejected' (留痕 · 不可 rollback)
//
//   Rollback (POST /ingest/regen-rollback) reads beforeSnapshot of an
//   `applied` entry and reverses the mutation, setting status='rolled_back'.
//
// IDB regenHistory store (idb.ts Day 4):
//   keyed by regenId · ordered by timestamp · permanently retained until the
//   user explicitly deletes (long-term value: content evolution timeline).
//
// I15/I16 invariants:
//   I15 — regen 2-phase: proposed entries never bleed into results until the
//          user confirms apply. Schema enforces by separating
//          beforeSnapshot + proposedItems from the actual results store.
//   I16 — rollback restores beforeSnapshot completely. Schema makes the
//          snapshot a structural array of items (passes through arbitrary
//          shapes via passthrough()), so any item kind can be saved.
import { z } from 'zod';
import { RegenActionSchema } from './regen-action.js';
/** Four-state machine for a regen entry. Linear: pending → (applied | rejected); applied → rolled_back. */
export const RegenStatusSchema = z.enum([
    'pending', // Phase 1 done · awaiting user confirm
    'applied', // Phase 2 apply done · live in results
    'rejected', // Phase 2 reject · proposed discarded · 留痕
    'rolled_back', // applied later rolled back · still in history with this status
]);
/** Cost accounting per regen call. Aggregated into per-user daily budget cap (acquireCourseLock). */
export const RegenCostSchema = z.object({
    tokensIn: z.number().int().min(0)
        .describe('Total input tokens consumed by the regen LLM call (0 for delete actions).'),
    tokensOut: z.number().int().min(0)
        .describe('Total output tokens emitted by the regen LLM call.'),
    costRmbEstimate: z.number().min(0)
        .describe('RMB estimate based on provider pricing at call time. Snapshot for audit (provider price may change later).'),
}).passthrough();
export const RegenHistoryEntrySchema = z.object({
    /** ULID. Stable across IDB / D1 / UI deep-link. */
    regenId: z.string().min(1).max(64)
        .describe('ULID. Unique across all regens of all courses.'),
    /** Owning course (matches CourseDraft.id and IngestPackage.courseId). */
    courseId: z.string().min(1).max(64)
        .describe('Owning courseId.'),
    /** ISO timestamp when Phase 1 started. */
    timestamp: z.string().datetime()
        .describe('ISO timestamp when Phase 1 began (LLM call started).'),
    /** The action that was requested. */
    action: RegenActionSchema
        .describe('The full RegenAction the user submitted.'),
    /**
     * Pre-mutation snapshot of items implicated by `action.scope`.
     * - For delete/replace: the items that would be removed.
     * - For add: empty array (nothing pre-existing to capture).
     * Used by rollback to reverse `applied`.
     */
    beforeSnapshot: z.array(z.unknown())
        .describe('Pre-mutation item snapshots (delete/replace) or [] (add). Drives rollback.'),
    /** Items the LLM produced. May be empty (delete actions skip LLM). */
    proposedItems: z.array(z.unknown())
        .describe('Items produced by the LLM (or computed for delete: empty).'),
    /** Workflow status. */
    status: RegenStatusSchema
        .describe('Phase-2 state machine: pending → (applied | rejected) → (rolled_back if applied).'),
    /** ISO timestamp when Phase 2 apply happened. Absent until applied. */
    appliedAt: z.string().datetime().optional()
        .describe('ISO timestamp of apply. Absent until status=applied.'),
    /** ISO timestamp when rollback happened. Absent until rolled back. */
    rolledBackAt: z.string().datetime().optional()
        .describe('ISO timestamp of rollback. Absent until status=rolled_back.'),
    /** Cost accounting (always present). */
    cost: RegenCostSchema
        .describe('Cost record (always present, even for delete: zeros).'),
}).passthrough();
/** Convenience: all 4 statuses as a const tuple, for tests / UI dispatchers. */
export const REGEN_STATUSES = ['pending', 'applied', 'rejected', 'rolled_back'];
//# sourceMappingURL=regen-history.js.map